/**
 * Filters Modal Plugin.
 *
 * Orchestrates the Filters modal open/close lifecycle. The modal shell itself (H1, close button,
 * empty body, CTA footer) is built by components/filtersModal.dom.js; the filter fields
 * inside the body are mounted separately (components/filterFields.dom.js): the shared
 * `@components/*Filter` components only produce HTML strings, and that file renders them into the
 * body and binds each one to its element. The fields edit a pending `filterDraft` in the store,
 * which only "Show XY Results" applies. The modal's own
 * open/close/stacking mechanics (Escape, overlay, activeModalStack) are entirely owned by the
 * `filtersModalService`/`createModal()` instance wired once in index.js — see
 * FILTERS_MODAL_PLUGIN_SEQUENCE.md. Teardown of the mounted filters is registered with the
 * service's `onHtmlRemoved`, which runs on every way the modal can close (X button, Escape, Show
 * XY Results, or `stop()`), so no close path can leave filter instances or their document/window
 * listeners behind. It also keeps the applied-filters count badge on the trigger button
 * (components/filterButtonBadge.dom.js) in sync for as long as the plugin runs.
 *
 * @module nativeInteractiveSiteMap/plugins/filtersModal.plugin
 */

import { fromEvent } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { parseDigits } from 'utils/numericInput';
import {
  FILTERS_MODAL_ID,
  createFiltersModalContent,
  bindFiltersModalClose,
  bindFiltersModalFooter,
  setClearAllButtonDisabled,
  setShowResultsCount,
} from '../components/filtersModal.dom';
import mountFilterFields from '../components/filterFields.dom';
import createFilterButtonBadgeDom from '../components/filterButtonBadge.dom';
import {
  selectAppliedFilterCount,
  selectSearchParameters,
  selectFilterDraft,
  selectFilterDraftDisplayedHousingType,
  selectFilterDraftHasActiveFilters,
  selectFilterDraftResultCount,
} from '../redux/selectors';
import { filtersModalAction$ } from '../redux/observables';
import { isSameFilterFieldValue } from '../services/filterResultsService';

// filterDraft stores range fields as { minimum, maximum }; every filter component's own
// getObservable()/setValue() speaks { min, max } instead (that's the shape the shared
// @components/numberSelect and Smart Range Selection utilities were built around, independently
// of this feature) — this is the one place that translates between the two.
function toMinMax({ minimum, maximum }) {
  return { min: minimum, max: maximum };
}

export default function createFiltersModalPlugin() {
  const state = {
    filtersModalService: null,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    subscriptions: [],
    unbindClose: null,
    unbindFooter: null,
    modalElement: null,
    filters: null,
    filterSubscriptions: [],
    footerSubscriptions: [],
    isOpen: false,
    isDismissal: false,
    filterButtonBadge: null,
  };

  function onAppliedFilterCountChanged(count) {
    state.filterButtonBadge?.setCount(count);
  }

  // The filter button's badge follows what's applied (searchParameters), not the draft, so it
  // changes only on "Show XY Results" — or on anything else that resets the applied filters
  // (e.g. viewReset) — and stays bound for the plugin's whole lifetime, modal open or not.
  function bindFilterButtonBadge(triggerButton) {
    state.filterButtonBadge = createFilterButtonBadgeDom(
      triggerButton,
      selectAppliedFilterCount(state.getState()),
    );
    return state.store$.pipe(
      map((storeState) => selectAppliedFilterCount(storeState)),
      distinctUntilChanged(),
    ).subscribe(onAppliedFilterCountChanged);
  }

  // Reflects whether any draft field is non-default onto the Clear all filters button
  // — disabled by default (the markup's own starting state). The greyed-out/enabled/hover looks
  // come for free from the shared `.nhs-n1-c-button--tertiary`'s own `[disabled]`/`:hover` styles
  // (see filtersModal.dom.js#setClearAllButtonDisabled) — this only flips the attribute.
  function onHasActiveFiltersChanged(hasActiveFilters) {
    setClearAllButtonDisabled(state.modalElement, !hasActiveFilters);
  }

  function onResultCountChanged(count) {
    setShowResultsCount(state.modalElement, count);
  }

  // Both footer buttons follow the draft reactively instead of being re-synced after each
  // dispatch: the store emits its current state on subscribe (so the count is right the moment
  // the modal opens — the max count, since nothing has narrowed the draft past what's applied
  // yet), and keeps emitting on every draft edit and on any data that settles while the modal is
  // open (e.g. the lot inventory finishing loading underneath it).
  function bindFooterState() {
    return [
      state.store$.pipe(
        map((storeState) => selectFilterDraftHasActiveFilters(storeState)),
        distinctUntilChanged(),
      ).subscribe(onHasActiveFiltersChanged),
      state.store$.pipe(
        map((storeState) => selectFilterDraftResultCount(storeState)),
        distinctUntilChanged(),
      ).subscribe(onResultCountChanged),
    ];
  }

  // Reflects the draft's values in the fields — on open (where the draft was just seeded from
  // what's applied, so reopening shows what's actually applied instead of always starting blank)
  // and after Clear all filters. Goes through each component's own setValue(), not the DOM
  // directly, and never through value$ — this is sync, not a user edit.
  function applyDraftValues() {
    const filterDraft = selectFilterDraft(state.getState());
    state.filters.price.setValue(toMinMax(filterDraft.price));
    state.filters.bedrooms.setValue(toMinMax(filterDraft.bedroom));
    state.filters.bathrooms.setValue(toMinMax(filterDraft.bathroom));
    state.filters.squareFeet.setValue(toMinMax(filterDraft.squareFeet));
    state.filters.moveInDate.setValue(filterDraft.moveInDateRange);
    // The displayed value, not the raw filterDraft.housingType — see
    // services/filterResultsService.js#getDisplayedHousingType (the Model-chip special case).
    state.filters.housingType.setValue(selectFilterDraftDisplayedHousingType(state.getState()));
  }

  // Writes one field's settled value into the draft, then reports the edit for tracking — after
  // the dispatch, so tracking.plugin reads the draft's new value. `field` is the filterDraft key.
  // Only an actual value change is reported: the fields also emit when they settle on the value
  // they already hold (SquareFeetFilter re-emits on every input blur, and re-picking the current
  // option of any dropdown emits it again).
  function changeDraftField(field, action) {
    const isChange = !isSameFilterFieldValue(field, selectFilterDraft(state.getState())[field], action.payload);
    state.dispatch(action);
    if (isChange) {
      filtersModalAction$.next({ action: 'updated', field });
    }
  }

  // Every field writes only into the store's filterDraft as it settles — nothing reaches
  // searchParameters (the map, the panel, the chip) until "Show XY Results" — nothing is
  // applied automatically before the CTA is clicked.
  function bindFilterDispatch() {
    const { price, bedrooms, bathrooms, squareFeet, moveInDate, housingType } = state.filters;
    const { dataActions } = state;

    return [
      price.getObservable().subscribe(({ min, max }) => {
        changeDraftField('price', dataActions.filterDraftPriceRangeChanged({ minimum: min, maximum: max }));
      }),
      bedrooms.getObservable().subscribe(({ min, max }) => {
        changeDraftField('bedroom', dataActions.filterDraftBedroomRangeChanged({ minimum: min, maximum: max }));
      }),
      bathrooms.getObservable().subscribe(({ min, max }) => {
        changeDraftField('bathroom', dataActions.filterDraftBathroomRangeChanged({ minimum: min, maximum: max }));
      }),
      // squareFeetFilter emits its raw field text ('Any' or '1,000'), not numbers — parseDigits
      // reads 'Any' as having no digits (null) the same way it reads a blank Price field.
      squareFeet.getObservable().subscribe(({ min, max }) => {
        changeDraftField('squareFeet', dataActions.filterDraftSquareFeetRangeChanged({
          minimum: parseDigits(min),
          maximum: parseDigits(max),
        }));
      }),
      moveInDate.getObservable().subscribe((key) => {
        changeDraftField('moveInDateRange', dataActions.filterDraftMoveInDateRangeChanged(key));
      }),
      housingType.getObservable().subscribe((key) => {
        changeDraftField('housingType', dataActions.filterDraftHousingTypeChanged(key));
      }),
    ];
  }

  // Runs from the modal's own close, whichever path triggered it — it must not call `clearHtml`.
  // Only a dismissal (X or Escape) is reported as 'closed': "Show XY Results" and stop() clear
  // isDismissal before closing. Reported first, while the draft still holds what's being left
  // unapplied.
  function teardownFilters() {
    if (state.isDismissal) {
      filtersModalAction$.next({ action: 'closed' });
    }
    state.isDismissal = false;

    state.unbindClose?.();
    state.unbindClose = null;

    state.unbindFooter?.();
    state.unbindFooter = null;

    state.filterSubscriptions.forEach((subscription) => subscription.unsubscribe());
    state.filterSubscriptions = [];

    state.footerSubscriptions.forEach((subscription) => subscription.unsubscribe());
    state.footerSubscriptions = [];

    Object.values(state.filters ?? {}).forEach((filter) => filter.destroy?.());
    state.filters = null;
    state.modalElement = null;

    state.isOpen = false;
  }

  function closeFiltersModal() {
    state.filtersModalService?.clearHtml();
  }

  // "Clear all filters" (footer). Resets only the draft's six Filters modal fields
  // (see dataSlice.js#filterDraftCleared) and re-syncs the fields the user is looking at; the
  // modal stays open and, like any other edit, nothing is applied until
  // "Show XY Results".
  function clearAllFilters() {
    filtersModalAction$.next({ action: 'cleared' });
    state.dispatch(state.dataActions.filterDraftCleared());
    // The footer is bound unconditionally (see openFiltersModal), but the fields only exist when
    // the body mounted — same guard teardownFilters uses.
    if (state.filters) {
      applyDraftValues();
    }
  }

  // "Show XY Results": applies the draft as it stands at click time to the map and
  // the details panel, then closes the modal back to the map. A "Model homes" Housing type pick
  // moved the draft's chip to Model — that switch goes through the same chipSelected the map chip
  // bar dispatches, so the listing resets and every chip-switch listener reacts exactly as it
  // would to a chip click.
  function applyFilters() {
    const draftLotStatus = selectFilterDraft(state.getState()).lotStatus;
    const appliedLotStatus = selectSearchParameters(state.getState()).lotStatus;

    // Reported before applying, while the draft and its result count are exactly what the user
    // clicked on.
    filtersModalAction$.next({ action: 'applied' });

    state.dispatch(state.dataActions.filtersApplied());
    if (draftLotStatus !== appliedLotStatus) {
      state.dispatch(state.dataActions.chipSelected(draftLotStatus));
    }

    state.isDismissal = false;
    closeFiltersModal();
  }

  function openFiltersModal() {
    // Seeds the draft from what's currently applied before anything reads it, so a previous
    // session's unapplied edits (closed with X/Escape) never leak into this one.
    state.dispatch(state.dataActions.filterDraftStarted());
    filtersModalAction$.next({ action: 'opened' });

    state.filtersModalService.setHtml({ payload: createFiltersModalContent() });
    state.filtersModalService.onHtmlRemoved({ name: FILTERS_MODAL_ID, fn: teardownFilters });
    state.isOpen = true;
    state.isDismissal = true;

    const modalElement = document.getElementById(FILTERS_MODAL_ID);
    state.modalElement = modalElement;
    const bodyElement = modalElement?.querySelector('[data-filters-modal-body]');
    if (bodyElement) {
      state.filters = mountFilterFields(bodyElement);
      applyDraftValues();
      state.filterSubscriptions = bindFilterDispatch();
    }

    state.footerSubscriptions = bindFooterState();
    state.unbindClose = bindFiltersModalClose(modalElement, closeFiltersModal);
    state.unbindFooter = bindFiltersModalFooter(modalElement, {
      onClearAll: clearAllFilters,
      onShowResults: applyFilters,
    });
  }

  return {
    name: 'filters-modal',

    async start(context) {
      const { filtersModalService, store$, getState, dispatch, dataActions } = context.services;
      if (!filtersModalService) {
        return;
      }

      const triggerButton = context.root.querySelector('[data-native-interactive-site-map-filters-opener]');
      if (!triggerButton) {
        return;
      }

      state.filtersModalService = filtersModalService;
      state.store$ = store$;
      state.getState = getState;
      state.dispatch = dispatch;
      state.dataActions = dataActions;
      state.subscriptions.push(
        fromEvent(triggerButton, 'click').subscribe(openFiltersModal),
        bindFilterButtonBadge(triggerButton),
      );
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      state.filterButtonBadge?.remove();
      state.filterButtonBadge = null;

      if (state.isOpen) {
        state.isDismissal = false;
        closeFiltersModal();
      }

      state.filtersModalService = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
    },
  };
}
